import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, LayoutDashboard, Key, Info, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import { useAuth } from '../../lib/auth';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/t3J9pG9VM9s2YNfBdSCinK/Logo_Kementerian_Lingkungan_Hidup_-_Badan_Pengendalian_Lingkungan_Hidup_(2024).png';
type LoginRole = 'officer' | 'admin';
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    login,
    isAuthenticated,
    user
  } = useAuth();
  const [role, setRole] = useState<LoginRole>('officer');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'admin') {
      setRole('admin');
    } else if (roleParam === 'officer') {
      setRole('officer');
    }
  }, [searchParams]);
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'officer') {
        navigate('/officer');
      } else {
        navigate('/command-center');
      }
    }
  }, [isAuthenticated, user, navigate]);
  // Reset form when switching roles
  useEffect(() => {
    setEmail('');
    setPassword('');
    setError('');
  }, [role]);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const success = await login(email, password, role);
    setIsLoading(false);
    if (success) {
      if (role === 'officer') {
        navigate('/officer');
      } else {
        navigate('/command-center');
      }
    } else {
      setError('Email atau password salah. Silakan coba lagi.');
    }
  };
  const fillDemoCredentials = () => {
    if (role === 'officer') {
      setEmail('petugas@demo.id');
      setPassword('petugas123');
    } else {
      setEmail('admin@demo.id');
      setPassword('admin123');
    }
    setError('');
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans" data-id="element-454">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-455">
        <div className="flex justify-center mb-4" data-id="element-456">
          <img src={LOGO_URL} alt="Logo Kementerian Lingkungan Hidup / BPLH" className="h-16 w-16 object-contain" data-id="element-457" />
        </div>
        <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900" data-id="element-458">
          Indonesia ASRI Hub
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600" data-id="element-459">
          Platform Nasional KIE Pengelolaan Sampah
        </p>
        <p className="mt-1 text-center text-xs text-gray-500" data-id="element-460">
          Kementerian Lingkungan Hidup / Badan Pengendalian Lingkungan Hidup
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-461">
        <Card className="border-0 shadow-xl" data-id="element-462">
          <CardHeader className="space-y-1 pb-4" data-id="element-463">
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg mb-4" data-id="element-464">
              <button onClick={() => setRole('officer')} className={`flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${role === 'officer' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-465">
                <Briefcase className="h-4 w-4" data-id="element-466" />
                Petugas
              </button>
              <button onClick={() => setRole('admin')} className={`flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${role === 'admin' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-467">
                <LayoutDashboard className="h-4 w-4" data-id="element-468" />
                Admin Pusat
              </button>
            </div>
            <CardTitle className="text-xl text-center" data-id="element-469">
              {role === 'officer' ? 'Login Petugas Lapangan' : 'Login Command Center'}
            </CardTitle>
            <CardDescription className="text-center" data-id="element-470">
              Masukkan kredensial Anda untuk melanjutkan
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-471">
            <form onSubmit={handleLogin} className="space-y-4" data-id="element-472">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-id="element-473">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-474" />
                  <p className="text-sm text-red-700" data-id="element-475">{error}</p>
                </div>}

              <div className="space-y-2" data-id="element-476">
                <label htmlFor="email" className="text-sm font-medium text-gray-700" data-id="element-477">
                  Email / NIP
                </label>
                <Input id="email" type="text" placeholder={role === 'officer' ? 'nip.petugas@klhk.go.id' : 'admin@klhk.go.id'} value={email} onChange={e => setEmail(e.target.value)} required data-id="element-478" />
              </div>
              <div className="space-y-2" data-id="element-479">
                <div className="flex items-center justify-between" data-id="element-480">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700" data-id="element-481">
                    Password
                  </label>
                  <a href="#" className="text-sm font-medium text-primary-600 hover:text-primary-500" data-id="element-482">
                    Lupa password?
                  </a>
                </div>
                <Input id="password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required data-id="element-483" />
              </div>

              <div className="flex items-center space-x-2" data-id="element-484">
                <input id="remember" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600" data-id="element-485" />
                <label htmlFor="remember" className="text-sm text-gray-500" data-id="element-486">
                  Ingat saya di perangkat ini
                </label>
              </div>

              <Button type="submit" className={`w-full ${role === 'officer' ? 'bg-secondary-600 hover:bg-secondary-700' : 'bg-gray-800 hover:bg-gray-900'}`} disabled={isLoading} data-id="element-487">
                {isLoading ? 'Memproses...' : 'Masuk Aplikasi'}
              </Button>

              {/* Demo Credentials Section */}
              <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-id="element-488">
                <div className="flex items-start gap-3" data-id="element-489">
                  <div className="bg-amber-100 p-1.5 rounded-full shrink-0" data-id="element-490">
                    <Key className="h-4 w-4 text-amber-700" data-id="element-491" />
                  </div>
                  <div className="flex-1" data-id="element-492">
                    <h4 className="font-semibold text-amber-900 flex items-center gap-2" data-id="element-493">
                      Akses Demo {role === 'officer' ? 'Petugas' : 'Admin'}
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200" data-id="element-494">
                        Simulasi
                      </span>
                    </h4>
                    <p className="text-amber-700 text-xs mt-1 mb-2" data-id="element-495">
                      Klik kredensial di bawah untuk isi otomatis:
                    </p>
                    <button type="button" onClick={fillDemoCredentials} className="w-full text-left bg-white border border-amber-200 rounded-md p-2 hover:bg-amber-50 hover:border-amber-300 transition-all group shadow-sm" data-id="element-496">
                      <div className="grid grid-cols-[40px_1fr] gap-1 font-mono text-xs text-amber-900" data-id="element-497">
                        <span className="text-amber-600" data-id="element-498">User:</span>
                        <span className="font-bold" data-id="element-499">
                          {role === 'officer' ? 'petugas@demo.id' : 'admin@demo.id'}
                        </span>
                        <span className="text-amber-600" data-id="element-500">Pass:</span>
                        <span className="font-bold" data-id="element-501">
                          {role === 'officer' ? 'petugas123' : 'admin123'}
                        </span>
                      </div>
                      <div className="text-[10px] text-amber-500 mt-1.5 font-medium flex items-center gap-1 group-hover:text-amber-700" data-id="element-502">
                        <Info className="h-3 w-3" data-id="element-503" />
                        Klik untuk auto-fill
                      </div>
                    </button>

                    {role === 'admin' && <div className="mt-2 pt-2 border-t border-amber-200" data-id="element-504">
                        <p className="text-[10px] text-amber-600 mb-1" data-id="element-505">
                          Akun demo lainnya:
                        </p>
                        <div className="grid grid-cols-2 gap-1 text-[10px] font-mono text-amber-800" data-id="element-506">
                          <span data-id="element-507">regional@demo.id</span>
                          <span data-id="element-508">regional123</span>
                          <span data-id="element-509">viewer@demo.id</span>
                          <span data-id="element-510">viewer123</span>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center border-t pt-6" data-id="element-511">
            <Link to="/portal" className="flex items-center text-sm text-gray-500 hover:text-gray-900" data-id="element-512">
              <ArrowLeft className="mr-2 h-4 w-4" data-id="element-513" />
              Kembali ke Portal Selector
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>;
}